import { Alert, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import get from 'lodash-es/get';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { API_S3_LOCATION_UPLOAD_INFO_URL } from '../../../constants/apiUrls';
import useBatchLocationJob from '../../../hooks/useBatchLocationJob';
import { useConfig } from '../../../hooks/useConfig';
import { useConstants } from '../../../hooks/useConstants';
import Link from '../../../inmarkit/Link';
import FormEndOptions from '../../forms/FormEndOptions';
import S3Upload from '../../forms/S3Upload';
import { generateLocationUploadKey, validateLocationFile } from './locationBatchUpload';

interface BatchLocationFormProps {
  adGroupId: number;
  adGroupName: string;
  lineItemId: number;
  momentsType: string;
  isUpdate?: boolean;
  onClose: () => void;
}

function BatchLocationForm({
  adGroupId,
  adGroupName,
  lineItemId,
  momentsType,
  isUpdate = false,
  onClose,
}: BatchLocationFormProps) {
  const { constants } = useConstants();
  const { apiHost } = useConfig();
  const [valid, setValid] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const { start, alert, setAlert } = useBatchLocationJob(adGroupId, isUpdate);
  const VENTURA_UPLOADS_PREFIX_LOCATIONS = get(constants, 'LOCATION.VENTURA_UPLOADS_PREFIX_LOCATIONS', '');
  const form = useForm({
    defaultValues: {
      adGroupId,
      key: undefined,
    },
  });
  let isValid = false;

  const { handleSubmit } = form;

  const handleCancelBtnClick = () => {
    onClose();
  };

  const generateUploadKey = () =>
    generateLocationUploadKey(VENTURA_UPLOADS_PREFIX_LOCATIONS, lineItemId, adGroupId, adGroupName);

  const onS3UploadFailure = ({ message }) => {
    setValid(false);
    setAlert({ severity: 'error', message });
  };

  const validateDuplicates = async (files) => {
    isValid = false;
    setValid(false);
    setUploadSuccess(false);

    const validation = await validateLocationFile(apiHost, momentsType, files[0]);
    isValid = validation.valid;
    setValid(isValid);
    if (!isValid) {
      setAlert({ severity: 'error', message: validation.message });
    }
  };

  const onS3UploadSuccess = () => {
    setUploadSuccess(true);
    // `valid` is used for React UI in render() and is not updated in time for this check.
    // `isValid` is not a React state and updated correctly.
    if (isValid) {
      setAlert({ severity: 'success', message: 'File upload successful. Select "Submit" to add locations to ad group.' });
    }
  };

  const onSubmit = ({ key }) => {
    if (!key) {
      setValid(false);
      setAlert({ severity: 'error', message: 'Sorry but the S3 key is missing. Please try attaching the file again.' });
      return;
    }
    start(key, { onSuccess: () => setValid(false) });
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...form}>
      <Stack spacing={1}>
        <Stack alignItems="flex-start" spacing={1}>
          <Typography variant="h5" fontWeight="bold">
            Batch {isUpdate ? 'Update' : 'Add'} Locations for {adGroupName}
          </Typography>
          <Paper>
            <Stack m={1} spacing={1}>
              <Typography variant="h5">Step 1: Upload File</Typography>
              {isUpdate && (
                <Typography>
                  Important: Only upload a file that was downloaded from this page. Please use download link below.
                  After you make your edits upload the file below. Note: you cannot change Id, campaign id, latitude,
                  longitude, or zipcode.
                </Typography>
              )}
              {!isUpdate && (
                <Typography>
                  <ul>
                    <li>
                      File format: Name, Address, Latitude, Longitude, Radius, Zipcode, Street, Location Type, State,
                      City, DMA, Congressional District, LV1, LV2, LV3, LV4, LV5, LV6, Targeting By, Bounded By,
                      Excluded, Preceptivity disabled
                    </li>
                    <li>All locations must have either lat+lon+radius and/or zipcode or Targeting</li>
                    <li>
                      If zipcode is provided along w/ lat+lon+radius, then the zipcode will only be used for reporting
                      purpose. The zipcode will not be used in the bidding operation of the campaign.
                    </li>
                    <li>
                      The value in Targeting By and Bounded By should be the corresponding shape type column name e.g.
                      state
                    </li>
                    <li>
                      Shapes that can be targeted or used for bounding: state, dma, zipcode, city,
                      congressional_district
                    </li>
                    <li>Header is required.</li>
                  </ul>
                </Typography>
              )}
              <Stack spacing={1}>
                <S3Upload
                  name="key"
                  s3InfoUrl={API_S3_LOCATION_UPLOAD_INFO_URL}
                  generateKey={generateUploadKey}
                  accept=".xlsx,.csv"
                  onSuccess={onS3UploadSuccess}
                  onFailure={onS3UploadFailure}
                  onStart={validateDuplicates}
                />
                {isUpdate && (
                  <Stack direction="row" justifyContent="center">
                    <Link href={`${apiHost}/admin/campaigns/${adGroupId}/locations/index_csv`}>
                      <Button variant="outlined">Download Existing Locations</Button>
                    </Link>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Paper>
          <Paper sx={{ width: 1 }}>
            <Stack m={1} spacing={1}>
              <Typography variant="h5">Step 2: Batch Process File</Typography>
              {alert && (
                <Stack direction="row" m={2}>
                  <Alert
                    sx={{ width: '100%' }}
                    severity={alert.severity}
                    action={alert.severity === 'info' ? <CircularProgress color="inherit" size={24} /> : undefined}
                  >
                    {alert.message}
                  </Alert>
                </Stack>
              )}
              <Stack direction="row" justifyContent="center">
                <Button disabled={!(valid && uploadSuccess)} onClick={handleSubmit(onSubmit)}>
                  Submit
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
        <FormEndOptions>
          <Stack direction="row" justifyContent="center" width="100%" spacing={2}>
            <Button variant="outlined" onClick={handleCancelBtnClick}>
              Close
            </Button>
          </Stack>
        </FormEndOptions>
      </Stack>
    </FormProvider>
  );
}

export default BatchLocationForm;
